import type { PublicSeoSerpTemplate } from "@/api/seo";

/**
 * Miroir de `DEFAULT_SEO_SERP_SETTINGS` (backend/src/config/seoSerp.js) —
 * garder les deux fichiers synchronisés, comme frontendRoutes.ts / .js.
 *
 * Ces gabarits doivent être disponibles **sans appel réseau** : les réglages
 * administrables ne sont chargés que côté client, donc sans eux le rendu SSR
 * retomberait sur `siteConfig.description` et servirait la même description à
 * toutes les pages. Aucun gabarit n'avance de chiffre invérifiable.
 */
export const SEO_SERP_DEFAULT_TEMPLATES: Record<string, PublicSeoSerpTemplate> =
  {
    home: {
      title: "Cours particuliers : trouvez le bon prof | {siteName}",
      description:
        "Décrivez votre besoin, nous vous proposons le professeur particulier qui correspond, à domicile ou en ligne. Réponse sous 48h, sans engagement.",
      canonicalPath: "/",
      breadcrumbTrail: "Accueil::/",
    },
    tutorsIndex: {
      title: "Professeurs particuliers disponibles | {siteName}",
      description:
        "Parcourez les professeurs particuliers inscrits sur {siteName}, comparez les matières, les formats de cours et les tarifs, puis contactez le vôtre.",
      canonicalPath: "/tuteurs",
      breadcrumbTrail: "Accueil::/ | Professeurs::/tuteurs",
    },
    courseSingle: {
      title: "Cours particuliers {slugLabel} : trouvez un prof | {siteName}",
      description:
        "Découvrez les cours particuliers {slugLabel} : profils de professeurs, formats de séance et tarifs horaires, avec un contact direct avec l'enseignant.",
      canonicalPath: "/cours/{slug}",
      breadcrumbTrail:
        "Accueil::/ | Cours::/cours | {slugLabel}::/cours/{slug}",
    },
    courseCitySubject: {
      title: "Cours de {subject} à {city} | {siteName}",
      description:
        "Trouvez un professeur de {subject} à {city} : approche pédagogique, disponibilités et tarif horaire. Contact direct avec l'enseignant, réponse sous 48h.",
      canonicalPath: "/cours/{citySlug}/{subjectSlug}",
      breadcrumbTrail:
        "Accueil::/ | Cours::/cours | {city}::/cours/{citySlug} | {subject}::/cours/{citySlug}/{subjectSlug}",
    },
    courseTutor: {
      title: "{listingTitle} - {tutorName} | {siteName}",
      description:
        'L\'annonce "{listingTitle}" de {tutorName}, professeur de {subject} à {city} : approche pédagogique, disponibilités et tarif horaire.',
      canonicalPath: "/cours/{citySlug}/{subjectSlug}/{tutorSlug}",
      breadcrumbTrail:
        "Accueil::/ | Cours::/cours | {city}::/cours/{citySlug} | {subject}::/cours/{citySlug}/{subjectSlug} | {listingTitle}::/cours/{citySlug}/{subjectSlug}/{tutorSlug}",
    },
    becomeTeacher: {
      title: "Devenir professeur particulier | {siteName}",
      description:
        "Publiez vos annonces de cours particuliers, fixez vos tarifs et gérez planning et paiements sur {siteName}. Inscription gratuite, sans engagement.",
      canonicalPath: "/devenir-professeur",
      breadcrumbTrail: "Accueil::/ | Devenir professeur::/devenir-professeur",
    },
    becomeTeacherFeature: {
      title: "{featureTitle} | {siteName}",
      description:
        'Comment fonctionne "{featureTitle}" sur {siteName}, et ce que cette fonctionnalité change au quotidien pour un professeur particulier.',
      canonicalPath: "/devenir-professeur/fonctionnalites/{featureSlug}",
      breadcrumbTrail:
        "Accueil::/ | Devenir professeur::/devenir-professeur | {featureTitle}::/devenir-professeur/fonctionnalites/{featureSlug}",
    },
    profLinker: {
      title: "ProfLinker : décrivez votre besoin | {siteName}",
      description:
        "Décrivez en une phrase le cours que vous cherchez : ProfLinker vous répond avec le profil de professeur qui correspond, et la raison de ce choix.",
      canonicalPath: "/proflinker",
      breadcrumbTrail: "Accueil::/ | ProfLinker::/proflinker",
    },
    discoverProfLinker: {
      title: "ProfLinker, la sélection sur mesure | {siteName}",
      description:
        "ProfLinker lit votre demande en langage naturel et vous oriente vers le professeur particulier adapté, avec l'explication du choix. Mode d'emploi.",
      canonicalPath: "/decouvrir-proflinker",
      breadcrumbTrail: "Accueil::/ | ProfLinker::/decouvrir-proflinker",
    },
    contact: {
      title: "Contacter l'équipe et poser une question | {siteName}",
      description:
        "Une question sur les cours particuliers, une demande en cours ou votre compte ? Écrivez à l'équipe {siteName}, nous vous répondons sous 48h.",
      canonicalPath: "/contact",
      breadcrumbTrail: "Accueil::/ | Contact::/contact",
    },
    demande: {
      title: "Demander un professeur particulier sous 48h | {siteName}",
      description:
        "Décrivez votre besoin en une phrase : matière, niveau, ville ou cours en ligne. L'équipe {siteName} vous répond sous 48h avec un professeur sélectionné.",
      canonicalPath: "/demande",
      breadcrumbTrail: "Accueil::/ | Demande::/demande",
    },
    blogIndex: {
      title: "Blog : conseils pour les cours particuliers | {siteName}",
      description:
        "Conseils, méthodes de travail et actualités scolaires pour progresser plus vite avec un professeur particulier, publiés par l'équipe {siteName}.",
      canonicalPath: "/blog",
      breadcrumbTrail: "Accueil::/ | Blog::/blog",
    },
    blogPost: {
      title: "{postTitle} | Blog {siteName}",
      description:
        "{postTitle} : la méthode détaillée, les erreurs à éviter et les points à travailler en priorité, sur le blog de {siteName}.",
      canonicalPath: "/blog/{postSlug}",
      breadcrumbTrail:
        "Accueil::/ | Blog::/blog | {postTitle}::/blog/{postSlug}",
    },
    pressReleasesIndex: {
      title: "Communiqués de presse officiels | {siteName}",
      description:
        "Retrouvez les annonces officielles et les actualités institutionnelles de {siteName}, plateforme de mise en relation pour les cours particuliers.",
      canonicalPath: "/press-releases",
      breadcrumbTrail: "Accueil::/ | Communiqués de presse::/press-releases",
    },
    pressReleasePost: {
      title: "{pressReleaseTitle} | {siteName}",
      description:
        'Le communiqué "{pressReleaseTitle}" publié par {siteName} : contexte de l\'annonce, contenu détaillé et contacts presse.',
      canonicalPath: "/press-releases/{pressReleaseSlug}",
      breadcrumbTrail:
        "Accueil::/ | Communiqués de presse::/press-releases | {pressReleaseTitle}::/press-releases/{pressReleaseSlug}",
    },
    legalNotice: {
      title: "Mentions légales et éditeur du site | {siteName}",
      description:
        "Éditeur, hébergeur, responsable de publication et coordonnées légales de {siteName}, plateforme de mise en relation pour les cours particuliers.",
      canonicalPath: "/mentions-legales",
      breadcrumbTrail: "Accueil::/ | Mentions légales::/mentions-legales",
    },
    cgu: {
      title: "Conditions générales d'utilisation | {siteName}",
      description:
        "Les règles d'utilisation de {siteName} : réservation et annulation des cours, paiements, obligations des élèves et des professeurs particuliers.",
      canonicalPath: "/cgu",
      breadcrumbTrail: "Accueil::/ | CGU::/cgu",
    },
    login: {
      title: "Connexion à votre espace personnel | {siteName}",
      description:
        "Connectez-vous à votre espace {siteName} pour suivre vos demandes de cours, votre planning, vos messages et vos paiements en un seul endroit.",
      canonicalPath: "/connexion",
      breadcrumbTrail: "Accueil::/ | Connexion::/connexion",
    },
    signup: {
      title: "Créer un compte élève ou professeur | {siteName}",
      description:
        "Créez gratuitement votre compte {siteName} pour trouver un professeur particulier ou proposer vos cours. Sans engagement, résiliable à tout moment.",
      canonicalPath: "/inscription",
      breadcrumbTrail: "Accueil::/ | Inscription::/inscription",
    },
  };

export const getDefaultSeoSerpTemplate = (pageType: string) =>
  SEO_SERP_DEFAULT_TEMPLATES[pageType];
